import React, {useEffect, useRef, useState} from "react";
import {createUUId} from "../../../../core/services/utils/utils";
import useIsMounted from "../../../hooks/use-is-mounted";
import {UploadStates} from "../../../../core/constants/enums";
import Api from "../../../../core/services/api/api";
import Dialog from "../../base/dialog";
import {DialogActions, DialogContent, DialogTitle, Divider, Fade} from "@mui/material";
import AddBannerImageDropZone from "../../app-specific/dropzone/add-banner-image";
import Button from "../../base/button/button";

const UploadBannerImageDialog = ({bannerId, onSuccess, bannerUrl, open, close}) => {
    const [uploadState, setUploadState] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [imageInfo, setImageInfo] = useState(null);
    const id = useRef(createUUId(true));
    const isMounted = useIsMounted();

    /**
     * With each change in the open state of the dialog:
     * - if the dialog is closed, resets the image info state.
     */
    useEffect(() => {
        if (!open) {
            setImageInfo(null);
        }
    }, [open])

    /**
     * Tracks the progress of the uploading tag icon, and updates the status based on it.
     * @param progressEvent {ProgressEvent}
     */
    const onUploadProgress = (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadState({
            state: UploadStates.pending,
            percentage: percentCompleted
        });
    }

    /**
     * Uploads the given image for the selected banner.
     *
     * * if the response of the api is successful, invokes the onSuccess callback with the new banner data then
     * closes the dialog.
     * @return {Promise<void>}
     */
    const uploadBannerImage = async () => {
        setSubmitting(true);
        const forApi = {
            id: bannerId,
            imageFileName: imageInfo.fileName,
            content: imageInfo.contentBytes
        }
        setUploadState({state: UploadStates.pending, percentage: 0});
        const response = await Api.updateBanner(forApi, onUploadProgress);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setUploadState({state: UploadStates.done, percentage: 0});
            onSuccess(response?.data);
            close();
        } else {
            setUploadState({state: UploadStates.error, percentage: 0});
        }
        setSubmitting(false);
    }

    /**
     * Creates a new banner with the given image info.
     *
     * * if the response of the api is successful, invokes the onSuccess callback with the new banner data then
     * closes the dialog.
     * @return {Promise<void>}
     */
    const createBanner = async () => {
        setSubmitting(true);
        const forApi = {
            imageFileName: imageInfo.fileName,
            content: imageInfo.contentBytes
        }
        setUploadState({state: UploadStates.pending, percentage: 0});
        const response = await Api.createNewBanner(forApi, onUploadProgress);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setUploadState({state: UploadStates.done, percentage: 0});
            onSuccess(response?.data);
            close();
        } else {
            setUploadState({state: UploadStates.error, percentage: 0});
        }
        setSubmitting(false);
    }

    /**
     * Submits the dialog.
     *
     * * if the banner exists, updates its image else creates a new banner.
     * @return {Promise<void>}
     */
    const onSubmit = () => {
        if (bannerId)
            return uploadBannerImage()
        else
            return createBanner();
    }

    return (
        <>
            <Dialog
                open={open}
                setOpen={() => close()}
                maxWidth={'sm'}
                className={'bound-dialog'}
                loading={submitting}
                disableEscapeKeyDown={submitting}
                disableBackdropClick={submitting}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <p className={`text-lg text-color-1 my-3 font-weight-bold text-center`}>
                        {
                            !!bannerId
                                ? "Change Banner Image"
                                : "Add New Banner"
                        }
                    </p>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-4 mb-4 mt-3'}>
                    <div id={id.current}>
                        <AddBannerImageDropZone
                            id={id.current}
                            uploadState={uploadState}
                            disabled={submitting}
                            onSuccess={setImageInfo}
                            url={bannerUrl}
                        />
                    </div>
                </DialogContent>
                <Divider className='divider'/>
                <DialogActions className={'d-flex align-items-center justify-content-between'} sx={{padding: 0}}>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Close
                    </Button>
                    <Fade in={!!imageInfo}>
                        <Button
                            disabled={submitting}
                            variant={'dialog'}
                            color={'primary'}
                            onClick={onSubmit}
                            borderRadius={'8px 0 8px 0'}
                        >
                            {
                                submitting
                                    ? "Submitting..."
                                    : !bannerId
                                        ? "Create"
                                        : "Save Changes"

                            }
                        </Button>
                    </Fade>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default UploadBannerImageDialog;
